import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ProfileField, Label } from '../../../types/profile';
import { isValidEmail } from '../../../utils/validationUtils';
import { profileService } from '../../../services';
import useDelayedEffect from '../../../hooks/useDelayedEffect';

interface EmailInputProps {
  field: ProfileField;
  setField: (field: ProfileField) => void;
  canBeEmpty?: boolean;
  emailCheck?: boolean;
  setAccountExists?: (exists: boolean) => void;
  mandatory?: boolean;
  validate?: boolean;
  accountExists?: boolean;
  showLabelSelector?: boolean;
  inputBgClassName?: string;
}

export default function EmailInput({
  field,
  setField,
  canBeEmpty = false,
  emailCheck = false,
  mandatory = false,
  validate = false,
  accountExists = false,
  setAccountExists = () => {},
  showLabelSelector = false,
  inputBgClassName = 'bg-secondary',
}: EmailInputProps): JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');

  useDelayedEffect(() => {
    if (!validate) return;
    if (accountExists) {
      setError(t('toast.error.general.accountExists'));
      return;
    }
    if (!field.email) {
      setError(canBeEmpty ? '' : t('invalid.empty'));
      return;
    }
    setError(isValidEmail(field.email) ? '' : t('invalid.invalid'));
  }, [field.email, validate, accountExists]);

  const onBlur = (): void => {
    if (!emailCheck || !field.email || !isValidEmail(field.email)) return;
    profileService
      .checkEmailExists(field.email)
      .then((exists): void => {
        setAccountExists(exists);
        setError('');
      })
      .catch(() => setError(t('invalid.invalid')));
  };

  return (
    <div className="relative flex flex-col">
      <label htmlFor={field.id} className="mb-1 pr-2 font-medium">
        {t('form.email.email')} {mandatory ? '*' : ''}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          id={field.id}
          onBlur={onBlur}
          placeholder={t('form.email.placeholder')}
          className={classNames(
            'h-10 flex-1 rounded-lg border px-2 text-gray-600 focus:ring-0 focus:ring-transparent',
            inputBgClassName,
            {
              '!border-error': error,
            },
          )}
          name="email"
          data-testid={`email-input-${field.id}`}
          type="text"
          value={field.email ?? ''}
          onChange={(e) => {
            setField({ ...field, email: e.target.value });
            if (accountExists) {
              setAccountExists(false);
            }
          }}
        />
        {showLabelSelector && (
          <select
            className="h-10 w-full rounded-lg border px-2 text-gray-600 focus:ring-0 focus:ring-transparent sm:w-32"
            value={field.label || Label.GENERAL}
            onChange={(e) => setField({ ...field, label: e.target.value as Label })}>
            <option value={Label.GENERAL}>{t('page.profile.labels.general')}</option>
            <option value={Label.PRIVATE}>{t('page.profile.labels.private')}</option>
            <option value={Label.WORK}>{t('page.profile.labels.work')}</option>
          </select>
        )}
      </div>
    </div>
  );
}
